import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material'
import { leaveApi } from '../../api/leaveApi'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'

const LeaveConfigurationDialog = ({ open, onClose }) => {
    const [loading, setLoading] = useState(false)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [isUpdate, setIsUpdate] = useState(false)
    const [leaveData, setLeaveData] = useState({
        paid_leaves: 1,
        unpaid_leaves: 1,
        sick_leaves: 1,
        maternity_leaves: 1,
        paternity_leaves: 1,
        casual_leaves: 1,
        compensatory_leaves: 0,
        org_id: null
    })
    const [initialLeaveData, setInitialLeaveData] = useState(null)

    // Get current user's org_id from redux
    const { user } = useSelector((state) => state.auth)
    const currentOrgId = user?.org_id || localStorage.getItem('org_id')

    useEffect(() => {
        if (open && currentOrgId) {
            fetchLeaveConfiguration()
        }
    }, [open, currentOrgId])

    const fetchLeaveConfiguration = async () => {
        setLoading(true)
        try {
            const response = await leaveApi.getOrgLeaves()
            // Check if response has data and it's not empty
            if (response.data && response.data.id) {
                const parsedData = {
                    paid_leaves: parseInt(response.data.paid_leaves, 10) || 0,
                    unpaid_leaves: parseInt(response.data.unpaid_leaves, 10) || 0,
                    sick_leaves: parseInt(response.data.sick_leaves, 10) || 0,
                    maternity_leaves: parseInt(response.data.maternity_leaves, 10) || 0,
                    paternity_leaves: parseInt(response.data.paternity_leaves, 10) || 0,
                    casual_leaves: parseInt(response.data.casual_leaves, 10) || 0,
                    compensatory_leaves: parseInt(response.data.compensatory_leaves, 10) || 0,
                    org_id: response.data.org_id || currentOrgId
                }
                setLeaveData(parsedData)
                setInitialLeaveData(parsedData)
                setIsUpdate(true)
            } else {
                // No config found or empty, initialize for create
                const initial = {
                    paid_leaves: 1,
                    unpaid_leaves: 1,
                    sick_leaves: 1,
                    maternity_leaves: 1,
                    paternity_leaves: 1,
                    casual_leaves: 1,
                    compensatory_leaves: 0,
                    org_id: currentOrgId
                }
                setLeaveData(initial)
                setInitialLeaveData(initial)
                setIsUpdate(false)
            }
        } catch (error) {
            console.error('Error fetching leave config:', error)
            // Reset to default on error/404
            setLeaveData({
                paid_leaves: 0,
                unpaid_leaves: 0,
                sick_leaves: 0,
                maternity_leaves: 0,
                paternity_leaves: 0,
                casual_leaves: 0,
                compensatory_leaves: 0,
                org_id: currentOrgId
            })
            setIsUpdate(false)
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target

        // Remove any non-digit characters
        const cleanValue = value.replace(/\D/g, '')

        // Remove leading zeros
        const intValue = parseInt(cleanValue, 10)

        setLeaveData(prev => ({
            ...prev,
            [name]: isNaN(intValue) ? 0 : intValue
        }))
    }

    const handleSubmit = async () => {
        if (initialLeaveData && JSON.stringify(leaveData) === JSON.stringify(initialLeaveData)) {
            toast('No changes to save');
            return;
        }
        setSubmitLoading(true)
        try {
            const payload = {
                ...leaveData,
                org_id: currentOrgId
            }

            if (isUpdate) {
                await leaveApi.updateOrgLeaves(payload)
                // Update initial data after successful update
                setInitialLeaveData(payload)
            } else {
                await leaveApi.createOrgLeaves(payload)
                // Update initial data after successful create
                setInitialLeaveData(payload)
            }
            onClose()
        } catch (error) {
            console.error('Error saving leave config:', error)
        } finally {
            setSubmitLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Leave Configuration</DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Configure the annual leave quotas for your organization. These values will be assigned to all employees by default.
                        </Alert>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Paid Leaves"
                                    name="paid_leaves"
                                    type="text"
                                    value={leaveData.paid_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Unpaid Leaves"
                                    name="unpaid_leaves"
                                    type="text"
                                    value={leaveData.unpaid_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Sick Leaves"
                                    name="sick_leaves"
                                    type="text"
                                    value={leaveData.sick_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Casual Leaves"
                                    name="casual_leaves"
                                    type="text"
                                    value={leaveData.casual_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Maternity Leaves"
                                    name="maternity_leaves"
                                    type="text"
                                    value={leaveData.maternity_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Paternity Leaves"
                                    name="paternity_leaves"
                                    type="text"
                                    value={leaveData.paternity_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Compensatory Leaves"
                                    name="compensatory_leaves"
                                    type="text"
                                    value={leaveData.compensatory_leaves}
                                    onChange={handleChange}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitLoading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || submitLoading}
                >
                    {submitLoading ? 'Saving...' : (isUpdate ? 'Update' : 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default LeaveConfigurationDialog
